import React, { useEffect, useRef, useState } from 'react';

import nextIcon from '@assets/img/icons/next.svg';
import pdfIcon from '@assets/img/icons/pdf.svg';
import prevIcon from '@assets/img/icons/prev.svg';
import { ActionableDialog, ActionableDialogTitle } from '@components/Dialog';
import { RemoteFile } from '@models/Files';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    title: '商品画像',
    prev: '前',
    next: '次',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    width: '100%',
  },
  paper: {
    maxWidth: '960px',
  },
  previewBox: {
    width: '100%',
    height: '100%',
  },
  previewPhoto: {
    width: '100%',
    height: '100%',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
  },
  border: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 4,
    padding: '7px 6px',
  },
  borderNormal: {
    borderWidth: 1,
    borderColor: theme.palette.grey[300],
  },
  borderSelected: {
    borderWidth: 3,
    borderColor: theme.palette.primary.main,
  },
  cursorPointer: {
    cursor: 'pointer',
  },
  largePhotoContainer: {
    backgroundColor: theme.palette.grey[50],
    ...common.flexInline,
    height: '408px',
    padding: '24px 0px',
  },
  switchButtonContainer: {
    width: '128px',
    ...common.flexCenter,
  },
  largePhoto: {
    width: '100%',
    height: '100%',
  },
  bottomContainer: {
    ...common.flexInline,
    backgroundColor: theme.palette.common.white,
    minHeight: '134px',
    padding: '19px 10px',
    overflowX: 'scroll',
    overflowY: 'hidden',
    '&::-webkit-scrollbar': {
      width: '0 !important',
    },
  },
  smallPhotosContainer: {
    margin: 'auto',
    ...common.flexInline,
    gap: '42px',
  },
  smallPhoto: {
    width: '95px',
    height: '95px',
  },
  pdfContainer: {
    ...common.exSmallerText,
    ...common.textAlignCenter,
    ...common.flexColumnCenter,
    ...common.textGray500,
    overflow: 'hidden',
    height: '100%',
    flex: 1,
    rowGap: '1rem',
  },
}));

interface CommodityPhotoPreviewProps {
  file: RemoteFile;
  border?: boolean;
  selected?: boolean;
  onClick?: () => void;
}

const CommodityPhotoPreview: React.FC<CommodityPhotoPreviewProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { file, border = false, selected = false, onClick } = props;

  return (
    <div
      className={clsx(
        classes.previewBox,
        onClick && classes.cursorPointer,
        border && clsx(classes.border, selected ? classes.borderSelected : classes.borderNormal),
      )}
      onClick={onClick}
    >
      {file.name.match(/\.(jpe?g|png|svg|webp|gif|bmp)$/i) && (
        <div className={classes.previewPhoto} style={{ backgroundImage: `url('${file.url}')` }} />
      )}
      {file.name.match(/\.pdf$/i) && (
        <div className={classes.pdfContainer}>
          <img src={pdfIcon} style={{ height: 32, width: 32 }} />
          <div
            style={{
              minHeight: 0,
              maxWidth: '100%',
              flexShrink: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {file.name}
          </div>
        </div>
      )}
    </div>
  );
};

interface CommdityPhotoSwitchButtonProps {
  next: boolean;
  onClick?: () => void;
}

const CommdityPhotoSwitchButton: React.FC<CommdityPhotoSwitchButtonProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { next, onClick } = props;

  return (
    <div onClick={onClick} className={classes.cursorPointer}>
      <img src={next ? nextIcon : prevIcon} alt={next ? strings.next : strings.prev} />
    </div>
  );
};

export interface CommodityPhotoBrowserProps {
  title?: string;
  urls: RemoteFile[];
  open?: boolean;
  onClose?: () => void;
}

export const CommodityPhotoBrowser: React.FC<CommodityPhotoBrowserProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { title = strings.title, urls, open = false, onClose } = props;
  const [photoIndex, setPhotoIndex] = useState<number>(0);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestIdleCallback(() => {
      const x = scrollRef?.current?.scrollLeft ?? 0;
      const { width } = scrollRef?.current?.getBoundingClientRect() ?? { width: 0 };
      const desiredX = photoIndex * 95 + (photoIndex ? (photoIndex - 1) * 44 : 0);
      if ((x < desiredX && desiredX + 95 - x > width) || x > desiredX) {
        scrollRef.current?.scrollTo(desiredX, 0);
      }
    });
  }, [photoIndex, scrollRef]);

  return (
    <ActionableDialog
      open={open}
      onClose={onClose}
      muiDialogProps={{
        disablePortal: true,
        fullWidth: true,
        maxWidth: 'md',
      }}
      classes={{
        paper: classes.paper,
      }}
    >
      <ActionableDialogTitle>{title}</ActionableDialogTitle>
      <div className={classes.largePhotoContainer}>
        <div className={classes.switchButtonContainer}>
          {photoIndex > 0 && (
            <CommdityPhotoSwitchButton
              next={false}
              onClick={() => {
                setPhotoIndex(photoIndex - 1);
              }}
            />
          )}
        </div>
        <div className={classes.largePhoto}>
          {urls[photoIndex] && (
            <CommodityPhotoPreview
              file={urls[photoIndex]}
              onClick={() => {
                if (urls[photoIndex]) {
                  window.open(urls[photoIndex].url, '_blank');
                }
              }}
            />
          )}
        </div>
        <div className={classes.switchButtonContainer}>
          {photoIndex < urls.length - 1 && (
            <CommdityPhotoSwitchButton
              next={true}
              onClick={() => {
                setPhotoIndex(photoIndex + 1);
              }}
            />
          )}
        </div>
      </div>
      <div className={classes.bottomContainer} ref={scrollRef}>
        <div className={classes.smallPhotosContainer}>
          {urls.map((url, index) => (
            <div className={classes.smallPhoto} key={index}>
              <CommodityPhotoPreview
                key={index}
                file={url}
                border={true}
                selected={index === photoIndex}
                onClick={() => {
                  setPhotoIndex(index);
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </ActionableDialog>
  );
};
